import type { ChangeEvent } from 'react';
import { type RefObject, useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';

import { sortOptions } from '../../../utils/sorting-and-filtering';
import type { Option, SelectOptionValue } from './SingleChoiceDropdown';
import {
  type DropdownSelection,
  getOptionLabel,
  getValueKey,
  type ResolvedDropdownValue,
  resolveDropdownSelection,
} from './single-choice-dropdown-utils';

interface UseSingleChoiceDropdownArgs<T extends SelectOptionValue> {
  value: T | Option<T> | null | undefined;
  getOptions: (search: string) => Promise<Option<T>[]> | Option<T>[];
  onChange?: (option: Option<T> | null) => void;
  isDisabled: boolean;
}

interface UseSingleChoiceDropdownResult<T extends SelectOptionValue> {
  isDropdownOpen: boolean;
  singleChoiceDropdownRef: RefObject<HTMLDivElement | null>;
  filteredOptions: Option<T>[];
  selectedOption: Option<T> | null;
  search: string;
  handleSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleOptionClick: (option: Option<T>) => void;
  handleClickOutside: () => void;
  toggleDropdown: () => void;
  openDropdown: () => void;
}

export function useSingleChoiceDropdown<T extends SelectOptionValue>({
  value,
  getOptions,
  onChange,
  isDisabled,
}: UseSingleChoiceDropdownArgs<T>): UseSingleChoiceDropdownResult<T> {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const singleChoiceDropdownRef = useRef<HTMLDivElement>(null);
  const [filteredOptions, setFilteredOptions] = useState<Option<T>[]>([]);
  const valueKey = getValueKey(value);
  const [resolvedValue, setResolvedValue] = useState<ResolvedDropdownValue<T> | null>(null);
  const [selection, setSelection] = useState<DropdownSelection<T> | null>(null);
  const { selectedOption, search } = resolveDropdownSelection(value, valueKey, resolvedValue, selection);
  const optionsRequestIdRef = useRef(0);

  const runOptionsQuery = useCallback(
    (query: string) => {
      const requestId = ++optionsRequestIdRef.current;
      void Promise.resolve(getOptions(query)).then(options => {
        if (optionsRequestIdRef.current !== requestId) return;
        setFilteredOptions(sortOptions(options, query, getOptionLabel));
      });
    },
    [getOptions],
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newSearch = event.target.value;
    setSelection({ valueKey, selectedOption, search: newSearch });
    setDropdownOpen(true);
    runOptionsQuery(newSearch);
  };

  const handleOptionClick = (option: Option<T>) => {
    setSelection({ valueKey, selectedOption: option, search: option.name });
    onChange?.(option);
    setDropdownOpen(false);
  };

  const handleClickOutside = () => {
    const wasOpen = isDropdownOpen;
    setDropdownOpen(false);
    const nextSearch = selectedOption?.name ?? '';
    setSelection({ valueKey, selectedOption, search: nextSearch });
    if (!selectedOption && wasOpen) {
      onChange?.(null);
    }
    // Refreshes the unfiltered list in the background so it's ready the next time the
    // dropdown opens.
    runOptionsQuery('');
  };

  const toggleDropdown = () => {
    if (!isDisabled) {
      setDropdownOpen(!isDropdownOpen);
    }
  };

  /**
   * Opens the list, and does nothing when it is already open.
   *
   * The field is served by both `focus` and `click`, which a single mouse press
   * fires in turn — opening rather than toggling keeps that one press from
   * closing what the focus just opened. It also gives the field a way back:
   * Escape closes the list and leaves the field focused, so `focus` alone never
   * fires again and there would be nothing left to reopen with.
   */
  const openDropdown = () => {
    if (!isDisabled) setDropdownOpen(true);
  };

  // Fetches the unfiltered option list once on mount, and again if the `getOptions` prop identity
  // ever changes. Per-keystroke fetching is handled directly by handleSearchChange above instead
  // of by reacting to the `search` state here, since calling a prop function from an effect that
  // watches state is an extra render users don't need to pay for
  // (https://react.dev/learn/you-might-not-need-an-effect).
  useEffect(() => {
    let active = true;
    const requestId = ++optionsRequestIdRef.current;
    void Promise.resolve(getOptions('')).then(options => {
      if (!active || optionsRequestIdRef.current !== requestId) return;
      setFilteredOptions(sortOptions(options, '', getOptionLabel));
    });
    return () => {
      active = false;
    };
  }, [getOptions]);

  useEffect(() => {
    if (value === undefined || value === null || typeof value === 'object') return;

    let active = true;
    void Promise.resolve(getOptions('')).then(options => {
      if (active) {
        setResolvedValue({
          valueKey,
          option: options.find(option => option.id === value) ?? null,
        });
      }
    });
    return () => {
      active = false;
    };
  }, [value, valueKey, getOptions]);

  const handleEscapeKey = useEffectEvent((event: globalThis.KeyboardEvent) => {
    if (event.key === 'Escape' && isDropdownOpen) {
      setDropdownOpen(false);
      const nextSearch = selectedOption?.name ?? '';
      setSelection({ valueKey, selectedOption, search: nextSearch });
      if (!selectedOption) {
        onChange?.(null);
      }
      runOptionsQuery('');
    }
  });

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isDropdownOpen]);

  return {
    isDropdownOpen,
    singleChoiceDropdownRef,
    filteredOptions,
    selectedOption,
    search,
    handleSearchChange,
    handleOptionClick,
    handleClickOutside,
    toggleDropdown,
    openDropdown,
  };
}
